import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import ReactDOM from 'react-dom';
import { Field } from 'react-final-form';

// ─────────────────────────────────────────────────────────────────────────
// Minimal, fast, dependency-free select for the bidding form.
//
// Why not react-select:
//   • react-select's JSS styles were overridden by legacy order.css in
//     unpredictable ways (missing borders mid-open, truncated option labels)
//   • layout shift on menu open because react-select mutated control height
//
// What this component does:
//   • controlled by react-final-form via <Field />
//   • trigger button + portal menu anchored with getBoundingClientRect()
//     (no overflow clipping, no legacy z-index conflicts)
//   • always-on type-to-search (case-insensitive substring)
//   • full keyboard nav: ↑ ↓ Home End Enter Esc, type-to-jump
//   • auto-flip above when not enough room below
//   • re-positions on scroll/resize
//   • zero external CSS dependencies (see `.bidding-select*` in form.scss)
// ─────────────────────────────────────────────────────────────────────────

const MENU_MAX_HEIGHT = 320;
const MENU_GAP = 6;

const Chevron = ({ open }) => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 14 14"
        aria-hidden="true"
        style={{
            transition: 'transform 150ms ease',
            transform: open ? 'rotate(180deg)' : 'none',
            flex: 'none',
        }}
    >
        <path
            d="M3.5 5.5L7 9L10.5 5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const Check = () => (
    <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden="true" style={{ flex: 'none', marginLeft: 8 }}>
        <path
            d="M3 7.5L6 10.5L11.5 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const XIcon = () => (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
);

function useOutsideClick(refs, onClose, enabled) {
    useEffect(() => {
        if (!enabled) return;
        const handler = (e) => {
            for (const ref of refs) {
                if (ref.current && ref.current.contains(e.target)) return;
            }
            onClose();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [refs, onClose, enabled]);
}

function usePopoverPosition(triggerRef, open, minWidth) {
    const [style, setStyle] = useState(null);

    const recompute = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        // Match the CSS `max-height: min(320px, 60vh)` rule so the
        // placement math doesn't flip the menu based on a height we'll
        // never actually render on short viewports.
        const effectiveMax = Math.min(MENU_MAX_HEIGHT, Math.round(vh * 0.6));
        const below = vh - r.bottom;
        const above = r.top;
        const placeAbove = below < effectiveMax + MENU_GAP && above > below;

        // Clamp the horizontal anchor so a narrow mobile viewport
        // never hosts a menu whose right edge overflows past the
        // window. 8px gutter on each side.
        const width = Math.min(Math.max(r.width, minWidth || 0), vw - 16);
        let left = Math.round(r.left);
        if (left + width > vw - 8) left = Math.max(8, vw - 8 - width);
        if (left < 8) left = 8;

        setStyle({
            position: 'fixed',
            left,
            top: Math.round(placeAbove ? r.top - MENU_GAP : r.bottom + MENU_GAP),
            width,
            maxHeight: effectiveMax,
            transform: placeAbove ? 'translateY(-100%)' : 'none',
            zIndex: 9999,
        });
    }, [triggerRef, minWidth]);

    useLayoutEffect(() => {
        if (!open) return;
        recompute();
        const onWin = () => recompute();
        window.addEventListener('scroll', onWin, true);
        window.addEventListener('resize', onWin);
        return () => {
            window.removeEventListener('scroll', onWin, true);
            window.removeEventListener('resize', onWin);
        };
    }, [open, recompute]);

    return style;
}

function SelectInner({
    input,
    meta,
    options,
    placeholder,
    disabled,
    isClearable,
    isSearchable,
    menuMinWidth,
    getOptionValue,
    getOptionLabel,
}) {
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const searchRef = useRef(null);
    const optionRefs = useRef([]);

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(-1);

    const showError = meta.touched && meta.error;

    const currentValue = useMemo(
        () => options.find((o) => String(getOptionValue(o)) === String(input.value)) || null,
        [options, input.value, getOptionValue],
    );

    const filtered = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => String(getOptionLabel(o)).toLowerCase().includes(q));
    }, [options, query, getOptionLabel]);

    const close = useCallback(() => {
        setOpen(false);
        setQuery('');
        setActive(-1);
    }, []);

    useOutsideClick([triggerRef, menuRef], close, open);

    // Open menu: focus search, point active at current selection
    useEffect(() => {
        if (!open) return;
        if (isSearchable && searchRef.current) searchRef.current.focus();
        const idx = filtered.findIndex((o) => o === currentValue);
        setActive(idx >= 0 ? idx : filtered.length > 0 ? 0 : -1);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep active option in view
    useEffect(() => {
        if (!open || active < 0) return;
        const node = optionRefs.current[active];
        if (node && node.scrollIntoView) {
            node.scrollIntoView({ block: 'nearest' });
        }
    }, [active, open]);

    // Clamp active when filter changes
    useEffect(() => {
        if (!open) return;
        if (active >= filtered.length) setActive(filtered.length > 0 ? 0 : -1);
    }, [filtered, open]); // eslint-disable-line react-hooks/exhaustive-deps

    const popoverStyle = usePopoverPosition(triggerRef, open, menuMinWidth);

    const selectValue = (opt) => {
        input.onChange(opt ? getOptionValue(opt) : '');
        close();
        // Return focus to trigger for keyboard users
        if (triggerRef.current) triggerRef.current.focus();
    };

    const onTriggerKeyDown = (e) => {
        if (disabled) return;
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
        }
    };

    const onMenuKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            close();
            if (triggerRef.current) triggerRef.current.focus();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(filtered.length - 1, a + 1));
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
            return;
        }
        if (e.key === 'Home') {
            e.preventDefault();
            setActive(0);
            return;
        }
        if (e.key === 'End') {
            e.preventDefault();
            setActive(filtered.length - 1);
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            if (active >= 0 && filtered[active]) selectValue(filtered[active]);
            return;
        }
        if (e.key === 'Tab') {
            close();
        }
    };

    const clear = (e) => {
        e.stopPropagation();
        input.onChange('');
    };

    const controlClass =
        'bidding-select__control' +
        (open ? ' is-open' : '') +
        (showError ? ' is-error' : '') +
        (disabled ? ' is-disabled' : '');

    return (
        <div className="bidding-select">
            <button
                ref={triggerRef}
                type="button"
                className={controlClass}
                aria-haspopup="listbox"
                aria-expanded={open}
                disabled={disabled}
                onClick={() => !disabled && setOpen((v) => !v)}
                onKeyDown={onTriggerKeyDown}
                onBlur={() => input.onBlur()}
                onFocus={() => input.onFocus()}
            >
                <span
                    className={
                        'bidding-select__value' +
                        (currentValue ? '' : ' is-placeholder')
                    }
                >
                    {currentValue ? getOptionLabel(currentValue) : placeholder || 'Select…'}
                </span>
                <span className="bidding-select__indicators">
                    {isClearable && currentValue && !disabled && (
                        <span
                            role="button"
                            tabIndex={-1}
                            aria-label="Clear"
                            className="bidding-select__clear"
                            onClick={clear}
                        >
                            <XIcon />
                        </span>
                    )}
                    <Chevron open={open} />
                </span>
            </button>

            {open && popoverStyle && ReactDOM.createPortal(
                <div
                    ref={menuRef}
                    className="bidding-select__menu"
                    style={popoverStyle}
                    onKeyDown={onMenuKeyDown}
                    role="listbox"
                >
                    {isSearchable && (
                        <div className="bidding-select__search">
                            <input
                                ref={searchRef}
                                type="text"
                                placeholder="Search…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={onMenuKeyDown}
                                autoComplete="off"
                            />
                        </div>
                    )}
                    <ul className="bidding-select__options">
                        {filtered.length === 0 && (
                            <li className="bidding-select__empty">No matches</li>
                        )}
                        {filtered.map((opt, idx) => {
                            const selected = currentValue === opt;
                            const isActive = idx === active;
                            return (
                                <li
                                    key={getOptionValue(opt)}
                                    ref={(el) => (optionRefs.current[idx] = el)}
                                    role="option"
                                    aria-selected={selected}
                                    className={
                                        'bidding-select__option' +
                                        (isActive ? ' is-active' : '') +
                                        (selected ? ' is-selected' : '')
                                    }
                                    onMouseEnter={() => setActive(idx)}
                                    onMouseDown={(e) => {
                                        // prevent blur before click
                                        e.preventDefault();
                                        selectValue(opt);
                                    }}
                                    title={getOptionLabel(opt)}
                                >
                                    <span className="bidding-select__option-label">
                                        {getOptionLabel(opt)}
                                    </span>
                                    {selected && <Check />}
                                </li>
                            );
                        })}
                    </ul>
                </div>,
                document.body,
            )}

            {showError && (
                <div className="bidding-select__error">{meta.error}</div>
            )}
        </div>
    );
}

export default function Select({
    name,
    options = [],
    placeholder = 'Select…',
    disabled = false,
    isClearable = false,
    isSearchable = true,
    menuMinWidth,
    getOptionValue = (o) => o.id,
    getOptionLabel = (o) => o.name,
}) {
    return (
        <Field name={name}>
            {({ input, meta }) => (
                <SelectInner
                    input={input}
                    meta={meta}
                    options={options}
                    placeholder={placeholder}
                    disabled={disabled}
                    isClearable={isClearable}
                    isSearchable={isSearchable}
                    menuMinWidth={menuMinWidth}
                    getOptionValue={getOptionValue}
                    getOptionLabel={getOptionLabel}
                />
            )}
        </Field>
    );
}
